<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { notify } from "@kyvg/vue3-notification"
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from "@/types/PageHeading"
import Button from "@/Components/Utils/Button.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

interface BankAccount {
    bank_name: string
    account_number: string
    account_name: string
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    setting: {
        company_name: string | null
        company_address: string | null
        company_phone: string | null
        terms: string[]
        bank_accounts: BankAccount[]
    }
}>()

const form = useForm({
    company_name: props.setting.company_name ?? "",
    company_address: props.setting.company_address ?? "",
    company_phone: props.setting.company_phone ?? "",
    terms: [...props.setting.terms],
    bank_accounts: props.setting.bank_accounts.map((account) => ({ ...account })),
})

const fieldError = (field: string) =>
    form.errors[field as keyof typeof form.errors] as string | undefined

const clearError = (field: string) => form.clearErrors(field as any)

const addTerm = () => form.terms.push("")
const removeTerm = (index: number) => form.terms.splice(index, 1)

const addBankAccount = () =>
    form.bank_accounts.push({ bank_name: "", account_number: "", account_name: "" })
const removeBankAccount = (index: number) => form.bank_accounts.splice(index, 1)

const submit = () => {
    form.terms = form.terms.map((term) => term.trim()).filter(Boolean)

    form.patch(route("kasir.settings.invoice.update"), {
        preserveScroll: true,
        onSuccess: () =>
            notify({
                title: "Tersimpan",
                text: "Pengaturan invoice berhasil diperbarui.",
                type: "success",
            }),
    })
}
</script>

<template>

    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-auto max-w-4xl px-4 py-6">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4">
                <h3 class="font-semibold text-gray-700">Identitas di invoice</h3>
                <p class="text-xs text-gray-400">
                    Tampil di bagian atas invoice pelanggan. Terpisah dari Profile Company di Master.
                </p>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Nama perusahaan <span
                            class="text-red-500">*</span></label>
                    <input v-model="form.company_name" placeholder="Eatpack.ku"
                        class="w-full rounded-md border-gray-300 text-sm"
                        :class="form.errors.company_name ? 'errorShake' : ''" @input="clearError('company_name')" />
                    <div v-if="form.errors.company_name" class="mt-1 text-xs text-red-500">
                        {{ form.errors.company_name }}
                    </div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Telepon</label>
                    <input v-model="form.company_phone" placeholder="085727112600 ; 085727191449"
                        class="w-full rounded-md border-gray-300 text-sm" />
                </div>

                <div class="md:col-span-2">
                    <label class="mb-1 block text-sm text-gray-600">Alamat</label>
                    <textarea v-model="form.company_address" rows="2"
                        placeholder="Jl. Dewi Sartika No. 2, Singocandi, Kec. Kota Kudus"
                        class="w-full rounded-md border-gray-300 text-sm" />
                </div>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4 flex items-center justify-between gap-x-4">
                <div>
                    <h3 class="font-semibold text-gray-700">Metode pembayaran</h3>
                    <p class="text-xs text-gray-400">Rekening tujuan transfer yang tampil di invoice.</p>
                </div>
                <Button size="xs" type="secondary" icon="fal fa-plus" label="Tambah rekening"
                    @click="addBankAccount" />
            </div>

            <div v-if="!form.bank_accounts.length"
                class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Belum ada rekening
            </div>

            <div v-else class="flex flex-col gap-3">
                <div v-for="(account, index) in form.bank_accounts" :key="`bank-account-${index}`"
                    class="flex items-start gap-x-3 rounded-lg border border-gray-200 p-4">
                    <div class="grid flex-1 gap-3 md:grid-cols-3">
                        <div v-for="field in [
                            { key: 'bank_name', label: 'Bank', placeholder: 'BCA' },
                            { key: 'account_number', label: 'No. rekening', placeholder: '0310473511' },
                            { key: 'account_name', label: 'Atas nama', placeholder: 'Fannidita Kurniawan' },
                        ]" :key="field.key">
                            <label class="mb-1 block text-xs text-gray-500">{{ field.label }}</label>
                            <input v-model="account[field.key as keyof BankAccount]" :placeholder="field.placeholder"
                                class="w-full rounded-md border-gray-300 text-sm"
                                :class="[field.key === 'account_number' ? 'font-mono' : '', fieldError(`bank_accounts.${index}.${field.key}`) ? 'errorShake' : '']"
                                @input="clearError(`bank_accounts.${index}.${field.key}`)" />
                            <div v-if="fieldError(`bank_accounts.${index}.${field.key}`)"
                                class="mt-1 text-xs text-red-500">
                                {{ fieldError(`bank_accounts.${index}.${field.key}`) }}
                            </div>
                        </div>
                    </div>
                    <button type="button" class="mt-6 pb-2 text-gray-400 hover:text-red-500"
                        @click="removeBankAccount(index)">
                        <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                    </button>
                </div>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4 flex items-center justify-between gap-x-4">
                <div>
                    <h3 class="font-semibold text-gray-700">Syarat & ketentuan</h3>
                    <p class="text-xs text-gray-400">Setiap poin tampil sebagai daftar bernomor di invoice.</p>
                </div>
                <Button size="xs" type="secondary" icon="fal fa-plus" label="Tambah poin" @click="addTerm" />
            </div>

            <div v-if="!form.terms.length"
                class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Belum ada syarat & ketentuan
            </div>

            <ol v-else class="flex flex-col gap-2">
                <li v-for="(term, index) in form.terms" :key="`term-${index}`" class="flex items-start gap-x-3">
                    <span class="w-5 pt-2 text-right text-sm text-gray-400">{{ index + 1 }}.</span>
                    <div class="flex-1">
                        <textarea v-model="form.terms[index]" rows="3"
                            class="w-full rounded-md border-gray-300 text-sm"
                            :class="fieldError(`terms.${index}`) ? 'errorShake' : ''"
                            @input="clearError(`terms.${index}`)" />
                        <div v-if="fieldError(`terms.${index}`)" class="mt-1 text-xs text-red-500">
                            {{ fieldError(`terms.${index}`) }}
                        </div>
                    </div>
                    <button type="button" class="pt-2 text-gray-400 hover:text-red-500" @click="removeTerm(index)">
                        <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                    </button>
                </li>
            </ol>
        </section>

        <div class="mt-4 flex justify-end">
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </div>
</template>
